import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast } from './Toast';
import { useSettings } from '../context/SettingsContext';
import NotificationBell from './NotificationBell';
import { hasParcelSession, isCustomerUser } from '../utils/sessionStorage';

export default function Navbar() {
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [diningMember, setDiningMember] = useState(null);
  const [diningSession, setDiningSession] = useState(null);
  const [parcelSession, setParcelSession] = useState(null);
  const [parcelCartCount, setParcelCartCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { hotelName, logoUrl } = useSettings();

  useEffect(() => {
    const updateCart = () => {
      const c = JSON.parse(localStorage.getItem('cart') || '[]');
      const parcelCart = JSON.parse(localStorage.getItem('parcelCart') || '[]');
      setCartCount(c.length);
      setParcelCartCount(parcelCart.reduce((sum, item) => sum + (item.qty || 0), 0));
    };
    const updateUser = () => {
      const t = JSON.parse(localStorage.getItem('user') || 'null');
      const member = JSON.parse(localStorage.getItem('diningMember') || 'null');
      const session = JSON.parse(localStorage.getItem('diningSession') || 'null');
      const parcel = JSON.parse(localStorage.getItem('parcelSession') || 'null');
      setUser(t);
      setDiningMember(member);
      setDiningSession(session);
      setParcelSession(parcel);
    };
    updateCart();
    updateUser();

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', () => {
      updateCart();
      updateUser();
    });

    // Listen for custom events (same-tab updates)
    const handleUserUpdate = () => {
      updateUser();
    };
    const handleCartUpdate = () => {
      updateCart();
    };
    window.addEventListener('userUpdated', handleUserUpdate);
    window.addEventListener('cartUpdated', handleCartUpdate);

    // Poll for changes every second to ensure immediate updates
    const interval = setInterval(() => {
      updateCart();
      updateUser();
    }, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', updateCart);
      window.removeEventListener('userUpdated', handleUserUpdate);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('diningSession');
    localStorage.removeItem('diningMember');
    localStorage.removeItem('diningTable');
    localStorage.removeItem('parcelSession');
    localStorage.removeItem('parcelCart');
    localStorage.removeItem('cart');
    setUser(null);
    setDiningMember(null);
    setDiningSession(null);
    setParcelSession(null);
    setCartCount(0);
    setParcelCartCount(0);
    // Dispatch event to update Navbar immediately
    window.dispatchEvent(new Event('userUpdated'));
    showToast('Logged out', 'success');
    navigate('/');
  };

  const isDiningGuest = Boolean(!user && diningSession?.sessionCode);
  const isParcelGuest = Boolean(!user && hasParcelSession());
  const customerCanOrder = isCustomerUser(user) || isDiningGuest;
  const publicGuest = isDiningGuest || isParcelGuest;
  const parcelName = parcelSession?.customerName || 'Your';
  const parcelLabel = parcelName === 'Your' ? 'Your' : `${parcelName}'s`;

  // Dynamically resolve hotel base path from URL pathname
  const match = window.location.pathname.match(/^\/hotel\/([^/]+)/);
  const hotelSlug = match ? match[1] : null;
  const hotelBasePath = hotelSlug ? `/hotel/${hotelSlug}` : '';

  const handleLogout = () => {
    logout();
    navigate(hotelBasePath || '/');
  };

  return (
    <nav className='sticky top-0 z-50 border-b border-amber-500/20'
      style={{ background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(16px)' }}
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-between items-center h-16'>
          <Link to={hotelBasePath || '/'} className='flex items-center space-x-2' onClick={() => setIsOpen(false)}>
            <div className='w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl overflow-hidden shadow-lg shadow-amber-500/30'>
              {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" /> : '🍽️'}
            </div>
            <span className='text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400'>
              {hotelName}
            </span>
          </Link>

          {/* Desktop Links */}
          <div className='hidden md:flex items-center space-x-6'>
            {/* Show Menu, About, Contact only to customers or logged out users */}
            {(user?.role === 'customer' || !user) && (
              <>
                <Link to={`${hotelBasePath}/menu`} className='text-gray-300 hover:text-amber-400 font-medium transition-colors'>
                  Menu
                </Link>
                {!hotelSlug && (
                  <>
                    <Link to={`${hotelBasePath}/about`} className='text-gray-300 hover:text-amber-400 font-medium transition-colors'>
                      About
                    </Link>
                    <Link to={`${hotelBasePath}/contact`} className='text-gray-300 hover:text-amber-400 font-medium transition-colors'>
                      Contact
                    </Link>
                  </>
                )}
              </>
            )}

            {user || publicGuest ? (
              <>
                <span className='text-gray-300 font-medium'>Hello, {user?.name || diningMember?.name || parcelSession?.customerName || 'Guest'}</span>
                {user && <NotificationBell />}

                {/* Role-specific navigation */}
                {customerCanOrder && (
                  <>
                    <Link
                      to={`${hotelBasePath}/my-orders`}
                      className='text-gray-300 hover:text-amber-400 font-medium transition-colors'
                    >
                      My Orders
                    </Link>
                    <Link
                      to={`${hotelBasePath}/cart`}
                      className='relative bg-amber-500/20 border border-amber-500/40 text-amber-300 px-4 py-2 rounded-lg font-semibold hover:bg-amber-500/30 transition-colors'
                    >
                      Cart ({cartCount})
                    </Link>
                  </>
                )}

                {isParcelGuest && (
                  <>
                    <Link
                      to={`${hotelBasePath}/parcel/checkout`}
                      className='text-gray-300 hover:text-amber-400 font-medium transition-colors'
                    >
                      My Order
                    </Link>
                    <Link
                      to={`${hotelBasePath}/parcel/menu`}
                      className='relative bg-amber-500/20 border border-amber-500/40 text-amber-300 px-4 py-2 rounded-lg font-semibold hover:bg-amber-500/30 transition-colors'
                    >
                      Cart ({parcelCartCount})
                    </Link>
                  </>
                )}

                {user?.role === 'owner' && (
                  <>
                    <Link
                      to={`${hotelBasePath}/admin`}
                      className='bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors'
                    >
                      Owner Dashboard
                    </Link>
                    <Link
                      to={`${hotelBasePath}/payments`}
                      className='bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors'
                    >
                      Payments
                    </Link>
                    <Link
                      to={`${hotelBasePath}/analytics`}
                      className='bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-600 transition-colors'
                    >
                      Analytics
                    </Link>
                  </>
                )}

                {user?.role === 'kitchen_staff' && (
                  <>
                    <Link
                      to={`${hotelBasePath}/kitchen`}
                      className='bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-600 transition-colors'
                    >
                      Kitchen Dashboard
                    </Link>
                    <Link
                      to={`${hotelBasePath}/payments`}
                      className='bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors'
                    >
                      Payments
                    </Link>
                  </>
                )}

                {user?.role === 'manager' && (
                  <>
                    <Link
                      to={`${hotelBasePath}/manager`}
                      className='bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors'
                    >
                      Manager Dashboard
                    </Link>
                    <Link
                      to={`${hotelBasePath}/payments`}
                      className='bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors'
                    >
                      Payments
                    </Link>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className='bg-white/10 border border-white/20 text-gray-300 px-4 py-2 rounded-lg font-semibold hover:bg-white/20 transition-colors'
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to={`${hotelBasePath}/login`}
                  className='text-gray-300 hover:text-amber-400 font-medium transition-colors'
                >
                  Login
                </Link>
                <Link
                  to={`${hotelBasePath}/register`}
                  className='bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all'
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Right Controls */}
          <div className='md:hidden flex items-center gap-2'>
            {customerCanOrder && (
              <Link
                to={`${hotelBasePath}/cart`}
                onClick={() => setIsOpen(false)}
                className='relative bg-amber-500/20 border border-amber-500/40 text-amber-300 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-amber-500/30 transition-colors'
              >
                Cart ({cartCount})
              </Link>
            )}
            {isParcelGuest && (
              <Link
                to={`${hotelBasePath}/parcel/checkout`}
                onClick={() => setIsOpen(false)}
                className='relative bg-amber-500/20 border border-amber-500/40 text-amber-300 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-amber-500/30 transition-colors'
              >
                {parcelLabel} Order
              </Link>
            )}
            {user && <NotificationBell />}
            
            {/* Mobile Hamburger Toggle Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-amber-400 focus:outline-none p-2 rounded-lg hover:bg-white/10 transition-all duration-300"
              aria-label="Toggle Menu"
            >
              {isOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer with AnimatePresence */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="md:hidden border-t border-white/10 px-4 py-4 space-y-2 overflow-hidden"
            style={{ background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)' }}
          >
            {/* Standard Guest Links */}
            {(user?.role === 'customer' || !user) && (
              <div className="flex flex-col space-y-1">
                <Link to={`${hotelBasePath}/menu`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                  Menu 🍽️
                </Link>
                {!hotelSlug && (
                  <>
                    <Link to={`${hotelBasePath}/about`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                      About Us ℹ️
                    </Link>
                    <Link to={`${hotelBasePath}/contact`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                      Contact 📞
                    </Link>
                  </>
                )}
              </div>
            )}

            {user || publicGuest ? (
              <div className="flex flex-col space-y-2 pt-2 border-t border-white/10">
                <div className="text-gray-400 font-bold px-3 py-1 text-sm bg-white/5 rounded-lg">
                  👤 Hello, {user?.name || diningMember?.name || parcelSession?.customerName || 'Guest'}
                </div>

                {customerCanOrder && (
                  <>
                    <Link to={`${hotelBasePath}/my-orders`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                      My Orders 📋
                    </Link>
                  </>
                )}

                {isParcelGuest && (
                  <>
                    <Link to={`${hotelBasePath}/parcel/menu`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                      Menu 🍽️
                    </Link>
                    <Link to={`${hotelBasePath}/parcel/checkout`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
                      My Order 📦
                    </Link>
                  </>
                )}

                {user?.role === 'owner' && (
                  <div className="flex flex-col space-y-1">
                    <Link to={`${hotelBasePath}/admin`} onClick={() => setIsOpen(false)} className="bg-orange-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-orange-600 transition-colors text-center shadow-sm">
                      Owner Dashboard 👑
                    </Link>
                    <Link to={`${hotelBasePath}/payments`} onClick={() => setIsOpen(false)} className="bg-green-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-green-600 transition-colors text-center shadow-sm">
                      Payments 💳
                    </Link>
                    <Link to={`${hotelBasePath}/analytics`} onClick={() => setIsOpen(false)} className="bg-purple-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-purple-600 transition-colors text-center shadow-sm">
                      Analytics 📊
                    </Link>
                  </div>
                )}

                {user?.role === 'kitchen_staff' && (
                  <div className="flex flex-col space-y-1">
                    <Link to={`${hotelBasePath}/kitchen`} onClick={() => setIsOpen(false)} className="bg-purple-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-purple-600 transition-colors text-center shadow-sm">
                      Kitchen Dashboard 🍳
                    </Link>
                    <Link to={`${hotelBasePath}/payments`} onClick={() => setIsOpen(false)} className="bg-green-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-green-600 transition-colors text-center shadow-sm">
                      Payments 💳
                    </Link>
                  </div>
                )}

                {user?.role === 'manager' && (
                  <div className="flex flex-col space-y-1">
                    <Link to={`${hotelBasePath}/manager`} onClick={() => setIsOpen(false)} className="bg-blue-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-blue-600 transition-colors text-center shadow-sm">
                      Manager Dashboard 💼
                    </Link>
                    <Link to={`${hotelBasePath}/payments`} onClick={() => setIsOpen(false)} className="bg-green-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:bg-green-600 transition-colors text-center shadow-sm">
                      Payments 💳
                    </Link>
                  </div>
                )}

                <button
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                  className="w-full bg-white/10 border border-white/20 text-gray-300 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/20 transition-colors text-center mt-2"
                >
                  Logout 🚪
                </button>
              </div>
            ) : (
              <div className="flex flex-col space-y-2 pt-2 border-t border-white/10">
                <Link to={`${hotelBasePath}/login`} onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-amber-400 font-semibold px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors text-center border border-white/20">
                  Login 🔑
                </Link>
                <Link to={`${hotelBasePath}/register`} onClick={() => setIsOpen(false)} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold px-3 py-2.5 rounded-lg hover:opacity-90 transition-all text-center shadow-sm">
                  Register 📝
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
